/**
 * Feature Gate Component (TSK-020)
 *
 * Unified feature gating that follows the principle:
 * "Always show, always explain, always provide upgrade path."
 *
 * Unlike traditional gating that hides features, this component:
 * 1. Always renders content (visible to all users)
 * 2. If user has access: renders normally
 * 3. If user lacks access: renders with disabled styling + upgrade badge
 * 4. On click (when gated): opens upgrade modal
 *
 * @example
 * // Wrap a form field (default variant)
 * <FeatureGate feature="custom_short_codes">
 *   <VanityUrlInput value={value} onChange={onChange} />
 * </FeatureGate>
 *
 * @example
 * // For navigation items (badge variant - just adds tier badge)
 * <FeatureGate feature="link_folders" variant="badge">
 *   <NavItem icon={FolderIcon}>Projects</NavItem>
 * </FeatureGate>
 *
 * @example
 * // For buttons (inline variant)
 * <FeatureGate feature="export_analytics" variant="inline">
 *   <Button>Export CSV</Button>
 * </FeatureGate>
 */

import { type ReactNode, useState } from 'react';
import { Crown, Lock, Sparkles } from 'lucide-react';
import { cn } from '@/client/lib/utils';
import { useFeatures, TIER_NAMES, type FeatureFlag } from '@/client/hooks/use-features';
import type { SubscriptionTier } from '@/client/hooks/use-auth';
import { UpgradeModal } from '@/client/components/modals/upgrade-modal';

// =============================================================================
// Types
// =============================================================================

export interface FeatureGateProps {
  /** The feature flag to check */
  feature: FeatureFlag;
  /** Content to render (always visible, but gated if user lacks access) */
  children: ReactNode;
  /** Custom upgrade message for the modal */
  upgradeMessage?: string;
  /** Additional class name for the wrapper */
  className?: string;
  /**
   * Rendering variant:
   * - 'default': Full wrapper with disabled overlay (for form fields, sections)
   * - 'badge': Just adds a tier badge after children (for nav items, inline text)
   * - 'inline': Inline wrapper for buttons/small elements
   * - 'card': Card-style wrapper with prominent upgrade CTA
   */
  variant?: 'default' | 'badge' | 'inline' | 'card';
  /**
   * Whether to show the tier badge (default: true)
   * Set to false if you want custom badge placement
   */
  showBadge?: boolean;
  /**
   * Callback when gated content is clicked
   * If provided, this is called instead of opening the upgrade modal
   */
  onGatedClick?: () => void;
  /**
   * For legacy compatibility: if true, hides content entirely when user lacks access
   * @deprecated Use the "always show" pattern instead
   */
  hideWhenLocked?: boolean;
}

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Badge showing the required tier
 */
export function TierBadge({
  tier,
  size = 'sm',
  className,
}: {
  tier: SubscriptionTier;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
}) {
  const tierName = TIER_NAMES[tier];

  const sizeClasses = {
    xs: 'px-1 py-0 text-[10px] gap-0.5',
    sm: 'px-1.5 py-0.5 text-xs gap-1',
    md: 'px-2 py-1 text-sm gap-1.5',
  };

  const tierColors: Record<SubscriptionTier, string> = {
    free: 'bg-muted text-muted-foreground',
    plus: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    pro: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-full shrink-0',
        sizeClasses[size],
        tierColors[tier],
        className
      )}
    >
      <Crown className={size === 'xs' ? 'size-2.5' : size === 'sm' ? 'size-3' : 'size-3.5'} />
      {tierName}
    </span>
  );
}

/**
 * Badge showing feature's required tier (only shown if user doesn't have access)
 */
export function FeatureBadge({
  feature,
  size = 'sm',
  className,
}: {
  feature: FeatureFlag;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
}) {
  const { hasFeature, getRequiredTier, isBeta } = useFeatures();

  if (hasFeature(feature)) return null;

  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      <TierBadge tier={getRequiredTier(feature)} size={size} />
      {isBeta(feature) && (
        <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-purple-600 bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400 px-1 py-0.5 rounded-full">
          <Sparkles className="size-2.5" />
          Beta
        </span>
      )}
    </span>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function FeatureGate({
  feature,
  children,
  upgradeMessage,
  className,
  variant = 'default',
  showBadge = true,
  onGatedClick,
  hideWhenLocked = false,
}: FeatureGateProps) {
  const { hasFeature, getRequiredTier, isBeta } = useFeatures();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const hasAccess = hasFeature(feature);
  const requiredTier = getRequiredTier(feature);
  const tierName = TIER_NAMES[requiredTier];
  const isFeatureBeta = isBeta(feature);

  // If user has access, render children without any gating UI
  if (hasAccess) {
    return <>{children}</>;
  }

  // Legacy behavior: hide entirely
  if (hideWhenLocked) {
    return null;
  }

  // User lacks access - show gated UI based on variant
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (onGatedClick) {
      onGatedClick();
    } else {
      setShowUpgradeModal(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e as unknown as React.MouseEvent);
    }
  };

  const ariaLabel = `Upgrade to ${tierName} to unlock this feature`;

  // Badge-only variant: just append the badge to children
  if (variant === 'badge') {
    return (
      <>
        <span
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          className={cn('inline-flex items-center gap-1.5 cursor-pointer', className)}
          role="button"
          tabIndex={0}
          aria-label={ariaLabel}
        >
          {children}
          {showBadge && <TierBadge tier={requiredTier} size="xs" />}
          {isFeatureBeta && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-purple-600 bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400 px-1 py-0.5 rounded-full">
              <Sparkles className="size-2.5" />
            </span>
          )}
        </span>
        <UpgradeModal
          open={showUpgradeModal}
          onOpenChange={setShowUpgradeModal}
          feature={feature}
          message={upgradeMessage}
        />
      </>
    );
  }

  // Inline variant: minimal wrapper for buttons/inline elements
  if (variant === 'inline') {
    return (
      <>
        <div
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          className={cn('relative inline-flex items-center cursor-pointer group', className)}
          role="button"
          tabIndex={0}
          aria-label={ariaLabel}
        >
          <div className="opacity-50 pointer-events-none">{children}</div>
          {showBadge && (
            <div className="ml-2">
              <TierBadge tier={requiredTier} size="xs" />
            </div>
          )}
        </div>
        <UpgradeModal
          open={showUpgradeModal}
          onOpenChange={setShowUpgradeModal}
          feature={feature}
          message={upgradeMessage}
        />
      </>
    );
  }

  // Card variant: prominent upgrade CTA for larger sections
  if (variant === 'card') {
    return (
      <>
        <div
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          className={cn(
            'relative cursor-pointer rounded-xl border-2 border-dashed border-muted-foreground/20',
            'hover:border-primary/40 hover:bg-primary/5 transition-all',
            'p-4',
            className
          )}
          role="button"
          tabIndex={0}
          aria-label={ariaLabel}
        >
          {/* Disabled content */}
          <div className="pointer-events-none opacity-40 select-none">{children}</div>

          {/* Upgrade overlay */}
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background/80 rounded-xl">
            <div className="flex items-center justify-center size-12 rounded-xl bg-amber-500/10">
              <Lock className="size-5 text-amber-600" />
            </div>
            <div className="text-center">
              <p className="font-medium">{tierName} Feature</p>
              <p className="text-sm text-muted-foreground">Click to upgrade</p>
            </div>
            {showBadge && <TierBadge tier={requiredTier} size="md" />}
          </div>
        </div>
        <UpgradeModal
          open={showUpgradeModal}
          onOpenChange={setShowUpgradeModal}
          feature={feature}
          message={upgradeMessage}
        />
      </>
    );
  }

  // Default variant: subtle wrapper with overlay
  return (
    <>
      <div
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={cn(
          'relative cursor-pointer rounded-lg transition-all',
          'ring-1 ring-inset ring-muted-foreground/10',
          'hover:ring-primary/30 hover:bg-primary/5',
          'group',
          className
        )}
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
      >
        {/* Disabled content overlay */}
        <div className="pointer-events-none opacity-50 select-none">{children}</div>

        {/* Upgrade badge - positioned in top-right corner */}
        {showBadge && (
          <div className="absolute top-2 right-2 flex items-center gap-1">
            <TierBadge tier={requiredTier} size="sm" />
            {isFeatureBeta && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-purple-600 bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400 px-1 py-0.5 rounded-full">
                <Sparkles className="size-2.5" />
              </span>
            )}
          </div>
        )}

        {/* Hover indicator */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-background/60 rounded-lg">
          <div className="flex items-center gap-2 text-sm font-medium text-primary">
            <Lock className="size-4" />
            <span>Click to unlock</span>
          </div>
        </div>
      </div>

      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        feature={feature}
        message={upgradeMessage}
      />
    </>
  );
}

// =============================================================================
// Hook for Custom Implementations
// =============================================================================

/**
 * Hook to check feature access and manage upgrade modal state
 * Useful for custom implementations where FeatureGate component isn't suitable
 *
 * @example
 * const { hasAccess, openUpgradeModal, UpgradeModalElement } = useFeatureGate('custom_short_codes');
 *
 * return (
 *   <>
 *     <Button onClick={hasAccess ? handleCreate : openUpgradeModal}>
 *       Create Custom URL
 *     </Button>
 *     {UpgradeModalElement}
 *   </>
 * );
 */
export function useFeatureGate(feature: FeatureFlag, customMessage?: string) {
  const { hasFeature, getRequiredTier, isBeta } = useFeatures();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const hasAccess = hasFeature(feature);
  const requiredTier = getRequiredTier(feature);
  const tierName = TIER_NAMES[requiredTier];
  const isFeatureBeta = isBeta(feature);

  return {
    hasAccess,
    requiredTier,
    tierName,
    isBeta: isFeatureBeta,
    showUpgradeModal,
    openUpgradeModal: () => setShowUpgradeModal(true),
    closeUpgradeModal: () => setShowUpgradeModal(false),
    setShowUpgradeModal,
    // Render the modal (include this in your component's JSX)
    UpgradeModalElement: (
      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        feature={feature}
        message={customMessage}
      />
    ),
  };
}

// Re-export FeatureFlag type for convenience
export type { FeatureFlag };
